import type {
	QcCustomerDetailResponse,
	QcFieldMeta,
	QcFieldMetaWithOptions,
	QcCustomerAction,
	SaveQcCustomerRequest,
} from '@/libs/fleet-api/services/qc-customer-types';
import type { FieldConfig, AccordionSection } from '@/pages/robotic/components/QCAccordionForm';
import dayjs from 'dayjs';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FieldMetaEntry = {
	editable: boolean;
	required: boolean;
	options?: { label: string; value: string }[];
	sourceEndpoint?: string;
};

export type FieldMetaMap = Record<string, FieldMetaEntry>;

type MappingResult = {
	values: Record<string, unknown>;
	fieldMeta: FieldMetaMap;
};

// ─── Apply API metadata to FieldConfig ──────────────────────────────────────

const applyMetaToField = (field: FieldConfig, meta: FieldMetaMap): FieldConfig => {
	const m = meta[field.name];
	if (!m) return field;
	return {
		...field,
		disabled: !m.editable,
		required: m.required,
		...(m.options?.length ? { options: m.options } : {}),
		...(m.sourceEndpoint ? { sourceEndpoint: m.sourceEndpoint } : {}),
	};
};

export const applyFieldMeta = (fields: FieldConfig[], meta: FieldMetaMap): FieldConfig[] =>
	fields.map((f) => applyMetaToField(f, meta));

export const applySectionsMeta = (sections: AccordionSection[], meta: FieldMetaMap): AccordionSection[] =>
	sections.map((s) => ({
		...s,
		fields: s.fields ? applyFieldMeta(s.fields, meta) : s.fields,
	}));

// ─── Helpers ────────────────────────────────────────────────────────────────

const extractMeta = (
	field: QcFieldMeta<unknown> & { sourceEndpoint?: string | null },
): FieldMetaEntry => ({
	editable: field.editable,
	required: field.required,
	...(field.sourceEndpoint ? { sourceEndpoint: field.sourceEndpoint } : {}),
});

const mapOptionsToSelect = (field: QcFieldMetaWithOptions<unknown>) =>
	(field.options ?? []).map((o) => ({
		label: o.value ?? '',
		value: String(o.id ?? ''),
	}));

// Pass-through select value mapper: keep raw API value (string) so submit preserves it.
// Display layer is responsible for blanking when value doesn't match any option.
const rawSelectValue = (field: QcFieldMetaWithOptions<number | null> | null | undefined): string | undefined =>
	field?.value != null ? String(field.value) : undefined;

const boolToRadio = (val: boolean | null | undefined): string | undefined => {
	if (val === true) return 'yes';
	if (val === false) return 'no';
	return undefined;
};

const radioToBool = (val: string | undefined): boolean | null => {
	if (val === 'yes') return true;
	if (val === 'no') return false;
	return null;
};

// ─── API Response → Form Values ─────────────────────────────────────────────

export const mapDetailToFormValues = (detail: QcCustomerDetailResponse): MappingResult => {
	const values: Record<string, unknown> = {};
	const fieldMeta: FieldMetaMap = {};

	const set = (name: string, field?: QcFieldMeta<unknown> | null) => {
		if (!field) return;
		values[name] = field.value;
		fieldMeta[name] = extractMeta(field);
	};

	const setStr = (name: string, field?: QcFieldMeta<string | null> | null) => {
		if (!field) return;
		values[name] = field.value ?? '';
		fieldMeta[name] = extractMeta(field);
	};

	const setBool = (name: string, field?: QcFieldMeta<boolean | null> | QcFieldMeta<boolean> | null) => {
		if (!field) return;
		values[name] = boolToRadio(field.value as boolean | null);
		fieldMeta[name] = extractMeta(field);
	};

	const setSelect = (name: string, field?: QcFieldMetaWithOptions<number | null> | null) => {
		if (!field) return;
		// Keep raw value so submit preserves it. Display layer (Select/table) blanks if no matching option.
		values[name] = field.value != null ? String(field.value) : undefined;
		const opts = mapOptionsToSelect(field);
		fieldMeta[name] = { ...extractMeta(field), ...(opts.length ? { options: opts } : {}) };
	};

	const setDate = (name: string, field?: QcFieldMeta<string | null> | null) => {
		if (!field) return;
		values[name] = field.value ? dayjs(field.value) : undefined;
		fieldMeta[name] = extractMeta(field);
	};

	// ── Customer Data (top fields) ──────────────────────────────────────────
	const cd = detail.customerData;
	if (cd) {
		setStr('namaSesuaiAkta', cd.legalName);
		setStr('npwp', cd.npwpNo);
		setStr('namaDataBank', cd.nameForBank);
		setStr('alamatLengkap', cd.address);
		setStr('rt', cd.rt);
		setStr('rw', cd.rw);
		setStr('kodePos', cd.postalCode);
		setStr('kelurahan', cd.subDistrict);
		setStr('kecamatan', cd.district);
		setStr('kota', cd.city);
		setStr('noTelepon', cd.phoneNo);
	}

	// ── Company Data ────────────────────────────────────────────────────────
	const cp = detail.companyData;
	if (cp) {
		setStr('tempatAktaDiterbitkan', cp.deedIssuedPlace);
		setStr('tahunPendirian', cp.deedFoundingYear);
		setStr('jenisPerusahaan', cp.companyType);
		setStr('emailPerusahaan', cp.companyEmail);
		set('jumlahKaryawan', cp.numberOfEmployees);
		setStr('groupBmpp', cp.groupBmppName);
		setSelect('targetMarket', cp.targetMarket);
		setBool('isGoPublic', cp.isGoPublic);
		setSelect('statusPerusahaan', cp.companyStatus);
	}

	// ── Legal Address (Alamat Sesuai Akta) ──────────────────────────────────
	const la = detail.legalAddress;
	if (la) {
		setStr('alamatLengkapAkta', la.address);
		setStr('rtAkta', la.rt);
		setStr('rwAkta', la.rw);
		setStr('kodeposAkta', la.postalCode);
		setStr('kelurahanAkta', la.subDistrict);
		setStr('kecamatanAkta', la.district);
		setStr('kotaAkta', la.city);
		setStr('noTeleponAkta', la.phoneNo);
		setStr('noHandphoneAkta', la.mobileNo);
	}

	// ── Operational Address ─────────────────────────────────────────────────
	const oa = detail.operationalAddress;
	if (oa) {
		setBool('useAlamatLegal', oa.useSameAsLegalAddress);
		setStr('alamatLengkapOperasional', oa.address);
		setStr('rtOperasional', oa.rt);
		setStr('rwOperasional', oa.rw);
		values['addressOperasional'] = {
			postalId: 0,
			postalCode: oa.postalCode?.value ?? '',
			locationName: oa.subDistrict?.value ?? '',
			districtName: oa.district?.value ?? '',
			cityName: oa.city?.value ?? '',
		};
		if (oa.postalCode) fieldMeta['addressOperasional'] = extractMeta(oa.postalCode);
		if (oa.subDistrict) fieldMeta['kelurahanOperasional'] = extractMeta(oa.subDistrict);
		if (oa.district) fieldMeta['kecamatanOperasional'] = extractMeta(oa.district);
		if (oa.city) fieldMeta['kotaOperasional'] = extractMeta(oa.city);
		setStr('noTeleponOperasional', oa.phoneNo);
	}

	// ── Financial Data ──────────────────────────────────────────────────────
	const fd = detail.financialData;
	if (fd) {
		set('penghasilanPerBulan', fd.monthlyIncomeAmount);
		set('rasioLancar', fd.currentRatioPercentage);
		set('tingkatRoi', fd.returnOnInvestment);
		set('rasioAngsuranPenghasilan', fd.installmentToIncomeRatioPercentage);
		setSelect('jenisRekeningBank', fd.bankAccountType);
		set('rataRataSaldoAkhir', fd.averageEndingBalanceAmount);
		set('rataRataSaldoMengendap', fd.averageDepositBalanceAmount);
		set('kasSetaraKas', fd.cashAndCashEquivalentAmount);
		set('piutangUsahaLancar', fd.tradeReceivableCurrentAssetAmount);
		set('investasiKeuanganLancar', fd.investmentOtherFinancialCurrentAssetAmount);
		set('asetLancarLainnya', fd.otherCurrentAssetAmount);
		set('totalAsetLancar', fd.totalCurrentAssetAmount);
		set('piutangUsahaTidakLancar', fd.tradeReceivableNonCurrentAssetAmount);
		set('investasiKeuanganTidakLancar', fd.investmentOtherFinancialNonCurrentAssetAmount);
		set('asetTidakLancarLainnya', fd.otherNonCurrentAssetAmount);
		set('totalAsetTidakLancar', fd.totalNonCurrentAssetAmount);
		set('totalAset', fd.totalAssetAmount);
		setSelect('statusTempatUsaha', fd.businessPlaceOwnershipStatus);
		setStr('menempatiTempatUsahaSejak', fd.occupiedSinceYear);
		setDate('tanggalBerakhirnyaSewa', fd.leaseEndDate);
		setStr('tahunLaporan', fd.reportYear);
		set('aktivaLancar', fd.currentAssetNonFixedAssetAmount);
		set('totalHutang', fd.totalDebtAmount);
		set('hutangJangkaPendek', fd.shortTermDebtAmount);
		set('modalDisetor', fd.paidInCapitalAmount);
		set('nilaiPenjualan', fd.salesValueAmount);
		set('pendapatanOperasional', fd.operationalIncomeAmount);
		set('labaRugiBruto', fd.grossProfitLossAmount);
		set('pendapatanNonOperasional', fd.nonOperationalIncomeAmount);
		set('labaRugiSebelumPajak', fd.profitLossBeforeTaxAmount);
		set('labaRugiTahunBerjalan', fd.currentYearProfitLossAmount);
		setBool('statusAudit', fd.isAudited);
		setStr('diAuditOleh', fd.auditedBy);
		set('pinjamanJangkaPendek', fd.shortTermLoanLiabilityAmount);
		set('utangUsahaJangkaPendek', fd.tradePayableShortTermLiabilityAmount);
		set('liabilitasJangkaPendekLainnya', fd.otherShortTermLiabilityAmount);
		set('liabilitasJangkaPendek', fd.totalShortTermLiabilityAmount);
		set('pinjamanJangkaPanjang', fd.longTermLoanLiabilityAmount);
		set('utangUsahaJangkaPanjang', fd.tradePayableLongTermLiabilityAmount);
		set('liabilitasJangkaPanjangLainnya', fd.otherLongTermLiabilityAmount);
		set('liabilitasJangkaPanjang', fd.totalLongTermLiabilityAmount);
		set('liabilitas', fd.totalLiabilityAmount);
		set('ekuitas', fd.equityAmount);
		set('totalPassiva', fd.totalPassiveAmount);
	}

	// ── Warehouse ───────────────────────────────────────────────────────────
	const wh = detail.warehouseOfficeWorkshop;
	if (wh) {
		setSelect('statusKepemilikanGudang', wh.ownershipStatus);
		setStr('alamatGudang', wh.address);
		setStr('rtGudang', wh.rt);
		setStr('rwGudang', wh.rw);
		setStr('kodeposGudang', wh.postalCode);
		setStr('kelurahanGudang', wh.subDistrict);
		setStr('kecamatanGudang', wh.district);
		setStr('kotaGudang', wh.city);
		setStr('noTeleponGudang', wh.phoneNo);
	}

	// ── Bank Account ────────────────────────────────────────────────────────
	const ba = detail.bankAccount;
	if (ba) {
		setStr('namaBank', ba.bankName);
		setStr('namaCabangBank', ba.branchName);
		setStr('nomorRekening', ba.accountNumber);
	}

	// ── Other Data ──────────────────────────────────────────────────────────
	const od = detail.otherData;
	if (od) {
		setBool('setujuPemasaranPihakKetiga', od.isAgreeThirdPartyMarketingPersonalComm);
		setBool('pernahKreditMobil', od.hasAppliedForVehicleCredit);
		setBool('daftarHitamAppi', od.isOnAppiBlacklist);
		setBool('setujuPemrosesan', od.isAgreeDataProcessingForFinancing);
		setBool('setujuPemasaranMtf', od.isAgreeMtfMarketingPersonalComm);
	}

	// ── PIC Document Signer ─────────────────────────────────────────────────
	const pic = detail.picDocumentSigner;
	if (pic) {
		setStr('namaPicTtd', pic.name);
		setStr('alamatLengkapPicTtd', pic.address);
		setStr('noKtpPicTtd', pic.idNumber);
		setStr('jabatanPicTtd', pic.position);
	}

	// ── PIC Billing ─────────────────────────────────────────────────────────
	const pb = detail.picBilling;
	if (pb) {
		setStr('namaPicPenagihan', pb.name);
		setStr('jabatanPicPenagihan', pb.position);
		setStr('noTeleponPicPenagihan', pb.phoneNumber);
		setStr('noTelepon2PicPenagihan', pb.phoneNumber2);
	}

	// ── Management Shareholders (table data) ────────────────────────────────
	if (detail.managementShareholders?.length) {
		values['sahamData'] = detail.managementShareholders.map((sh) => ({
			nama: sh.name?.value ?? '',
			kewarganegaraan: rawSelectValue(sh.citizenship),
			kebangsaan: sh.nationality?.value ?? '',
			jenisIdentitas: rawSelectValue(sh.identityCardType),
			noIdentitas: sh.identityNumber?.value ?? '',
			tanggalLahir: sh.dateOfBirth?.value ? dayjs(sh.dateOfBirth.value) : undefined,
			jenisKelamin: rawSelectValue(sh.gender),
			noTelepon: sh.phoneNumber?.value ?? '',
			jabatan: rawSelectValue(sh.position),
			alamat: sh.address?.value ?? '',
			kodePos: sh.postalCode?.value ?? '',
			kelurahan: sh.subDistrict?.value ?? '',
			kecamatan: sh.district?.value ?? '',
			kota: sh.city?.value ?? '',
			rt: sh.rt?.value ?? '',
			rw: sh.rw?.value ?? '',
			status: rawSelectValue(sh.status),
			isShareholder: sh.isShareholder?.value,
			persentaseKepemilikan: sh.shareholding?.value,
			identityFile: sh.identityFile
				? { id: sh.identityFile.id, name: sh.identityFile.name, url: (sh.identityFile as { value?: string }).value }
				: undefined,
		}));
		// Per-row meta — keyed by FE field name in QCSahamModal
		values['sahamDataMeta'] = detail.managementShareholders.map((sh) => {
			const m: FieldMetaMap = {};
			if (sh.name) m['nama'] = extractMeta(sh.name);
			if (sh.citizenship) m['kewarganegaraan'] = { ...extractMeta(sh.citizenship), options: mapOptionsToSelect(sh.citizenship) };
			if (sh.nationality) m['nationality'] = extractMeta(sh.nationality);
			if (sh.identityCardType) m['jenisIdentitas'] = { ...extractMeta(sh.identityCardType), options: mapOptionsToSelect(sh.identityCardType) };
			if (sh.identityNumber) m['noId'] = extractMeta(sh.identityNumber);
			if (sh.identityFile) m['identityFile'] = extractMeta(sh.identityFile);
			if (sh.dateOfBirth) m['tanggalLahir'] = extractMeta(sh.dateOfBirth);
			if (sh.gender) m['jenisKelamin'] = { ...extractMeta(sh.gender), options: mapOptionsToSelect(sh.gender) };
			if (sh.phoneNumber) m['noTelepon'] = extractMeta(sh.phoneNumber);
			if (sh.position) m['jabatan'] = { ...extractMeta(sh.position), options: mapOptionsToSelect(sh.position) };
			if (sh.address) m['alamat'] = extractMeta(sh.address);
			if (sh.postalCode) m['kodePos'] = extractMeta(sh.postalCode);
			if (sh.subDistrict) m['kelurahan'] = extractMeta(sh.subDistrict);
			if (sh.district) m['kecamatan'] = extractMeta(sh.district);
			if (sh.city) m['kota'] = extractMeta(sh.city);
			if (sh.rt) m['rt'] = extractMeta(sh.rt);
			if (sh.rw) m['rw'] = extractMeta(sh.rw);
			if (sh.status) m['status'] = { ...extractMeta(sh.status), options: mapOptionsToSelect(sh.status) };
			if (sh.isShareholder) m['isManagement'] = extractMeta(sh.isShareholder);
			if (sh.shareholding) m['kepemilikanSaham'] = extractMeta(sh.shareholding);
			return m;
		});
	}

	// ── Legal Documents (table data) ────────────────────────────────────────
	if (detail.legalDocuments?.length) {
		values['legalitasDokumen'] = detail.legalDocuments.map((doc) => ({
			jenis: doc.documentType?.value ?? '',
			noDokumen: doc.documentNumber?.value ?? '',
			tanggalDokumen: doc.documentDate?.value ? dayjs(doc.documentDate.value) : null,
			notes: doc.notes?.value ?? '',
		}));
	}

	return { values, fieldMeta };
};

// ─── Form Values → Save/Submit Request ──────────────────────────────────────

export const mapFormValuesToSaveRequest = (
	customerId: string,
	values: Record<string, unknown>,
	action: QcCustomerAction,
): SaveQcCustomerRequest => {
	const str = (key: string) => (values[key] as string) || null;
	const num = (key: string) => {
		const v = values[key];
		return v != null && v !== '' ? Number(v) : null;
	};
	const int = (key: string) => {
		const v = values[key];
		return v != null && v !== '' ? parseInt(String(v), 10) : null;
	};
	const bool = (key: string) => radioToBool(values[key] as string | undefined);
	const dateStr = (key: string) => {
		const v = values[key];
		if (!v) return null;
		if (typeof v === 'object' && 'format' in (v as object)) {
			return (v as dayjs.Dayjs).format('YYYY-MM-DD');
		}
		return String(v);
	};

	return {
		customerId,
		action,
		customerData: {
			legalName: str('namaSesuaiAkta'),
			nameForBank: str('namaDataBank'),
			npwpNo: str('npwp'),
			address: str('alamatLengkap'),
			rt: str('rt'),
			rw: str('rw'),
			postalCode: str('kodePos'),
			phoneNo: str('noTelepon'),
		},
		companyData: {
			deedIssuedPlace: str('tempatAktaDiterbitkan'),
			deedFoundingYear: str('tahunPendirian'),
			companyType: str('jenisPerusahaan'),
			companyEmail: str('emailPerusahaan'),
			numberOfEmployees: num('jumlahKaryawan') as number | null,
			groupBmppName: str('groupBmpp'),
			targetMarket: int('targetMarket'),
			isGoPublic: bool('isGoPublic'),
			companyStatus: int('statusPerusahaan'),
		},
		legalAddress: {
			address: str('alamatLengkapAkta'),
			rt: str('rtAkta'),
			rw: str('rwAkta'),
			postalCode: str('kodeposAkta'),
			phoneNo: str('noTeleponAkta'),
			mobileNo: str('noHandphoneAkta'),
		},
		operationalAddress: {
			useSameAsLegalAddress: bool('useAlamatLegal'),
			address: str('alamatLengkapOperasional'),
			rt: str('rtOperasional'),
			rw: str('rwOperasional'),
			postalCode: (values['addressOperasional'] as { postalCode?: string })?.postalCode || null,
			phoneNo: str('noTeleponOperasional'),
		},
		financialData: {
			monthlyIncomeAmount: num('penghasilanPerBulan'),
			currentRatioPercentage: num('rasioLancar'),
			returnOnInvestment: num('tingkatRoi'),
			installmentToIncomeRatioPercentage: num('rasioAngsuranPenghasilan'),
			bankAccountType: int('jenisRekeningBank'),
			averageEndingBalanceAmount: num('rataRataSaldoAkhir'),
			averageDepositBalanceAmount: num('rataRataSaldoMengendap'),
			cashAndCashEquivalentAmount: num('kasSetaraKas'),
			tradeReceivableCurrentAssetAmount: num('piutangUsahaLancar'),
			investmentOtherFinancialCurrentAssetAmount: num('investasiKeuanganLancar'),
			otherCurrentAssetAmount: num('asetLancarLainnya'),
			totalCurrentAssetAmount: num('totalAsetLancar'),
			tradeReceivableNonCurrentAssetAmount: num('piutangUsahaTidakLancar'),
			investmentOtherFinancialNonCurrentAssetAmount: num('investasiKeuanganTidakLancar'),
			otherNonCurrentAssetAmount: num('asetTidakLancarLainnya'),
			totalNonCurrentAssetAmount: num('totalAsetTidakLancar'),
			totalAssetAmount: num('totalAset'),
			businessPlaceOwnershipStatus: int('statusTempatUsaha'),
			occupiedSinceYear: str('menempatiTempatUsahaSejak'),
			leaseEndDate: dateStr('tanggalBerakhirnyaSewa'),
			reportYear: str('tahunLaporan'),
			currentAssetNonFixedAssetAmount: num('aktivaLancar'),
			totalDebtAmount: num('totalHutang'),
			shortTermDebtAmount: num('hutangJangkaPendek'),
			paidInCapitalAmount: num('modalDisetor'),
			salesValueAmount: num('nilaiPenjualan'),
			operationalIncomeAmount: num('pendapatanOperasional'),
			grossProfitLossAmount: num('labaRugiBruto'),
			nonOperationalIncomeAmount: num('pendapatanNonOperasional'),
			profitLossBeforeTaxAmount: num('labaRugiSebelumPajak'),
			currentYearProfitLossAmount: num('labaRugiTahunBerjalan'),
			isAudited: bool('statusAudit'),
			auditedBy: str('diAuditOleh'),
			shortTermLoanLiabilityAmount: num('pinjamanJangkaPendek'),
			tradePayableShortTermLiabilityAmount: num('utangUsahaJangkaPendek'),
			otherShortTermLiabilityAmount: num('liabilitasJangkaPendekLainnya'),
			totalShortTermLiabilityAmount: num('liabilitasJangkaPendek'),
			longTermLoanLiabilityAmount: num('pinjamanJangkaPanjang'),
			tradePayableLongTermLiabilityAmount: num('utangUsahaJangkaPanjang'),
			otherLongTermLiabilityAmount: num('liabilitasJangkaPanjangLainnya'),
			totalLongTermLiabilityAmount: num('liabilitasJangkaPanjang'),
			totalLiabilityAmount: num('liabilitas'),
			equityAmount: num('ekuitas'),
			totalPassiveAmount: num('totalPassiva'),
		},
		warehouseOfficeWorkshop: {
			ownershipStatus: int('statusKepemilikanGudang'),
			address: str('alamatGudang'),
			rt: str('rtGudang'),
			rw: str('rwGudang'),
			postalCode: str('kodeposGudang'),
			subDistrict: str('kelurahanGudang'),
			district: str('kecamatanGudang'),
			city: str('kotaGudang'),
			phoneNo: str('noTeleponGudang'),
		},
		bankAccount: {
			bankName: str('namaBank'),
			branchName: str('namaCabangBank'),
			accountNumber: str('nomorRekening'),
		},
		otherData: {
			isAgreeThirdPartyMarketingPersonalComm: bool('setujuPemasaranPihakKetiga'),
			hasAppliedForVehicleCredit: bool('pernahKreditMobil'),
			isOnAppiBlacklist: bool('daftarHitamAppi'),
			isAgreeDataProcessingForFinancing: bool('setujuPemrosesan'),
			isAgreeMtfMarketingPersonalComm: bool('setujuPemasaranMtf'),
		},
		picDocumentSigner: {
			name: str('namaPicTtd'),
			address: str('alamatLengkapPicTtd'),
			idNumber: str('noKtpPicTtd'),
			position: str('jabatanPicTtd'),
		},
		picBilling: {
			name: str('namaPicPenagihan'),
			position: str('jabatanPicPenagihan'),
			phoneNumber: str('noTeleponPicPenagihan'),
			phoneNumber2: str('noTelepon2PicPenagihan'),
		},
		managementShareholders: ((values['sahamData'] as Record<string, unknown>[]) ?? []).map((sh) => ({
			name: (sh.nama as string) || null,
			citizenship: sh.kewarganegaraan != null ? parseInt(String(sh.kewarganegaraan), 10) : null,
			nationality: (sh.kebangsaan as string) || null,
			identityCardType: sh.jenisIdentitas != null ? parseInt(String(sh.jenisIdentitas), 10) : null,
			identityNumber: (sh.noIdentitas as string) || null,
			dateOfBirth: sh.tanggalLahir && typeof sh.tanggalLahir === 'object' && 'format' in (sh.tanggalLahir as object)
				? (sh.tanggalLahir as dayjs.Dayjs).format('YYYY-MM-DD')
				: null,
			gender: sh.jenisKelamin != null ? parseInt(String(sh.jenisKelamin), 10) : null,
			phoneNumber: (sh.noTelepon as string) || null,
			position: sh.jabatan != null ? parseInt(String(sh.jabatan), 10) : null,
			address: (sh.alamat as string) || null,
			postalCode: (sh.kodePos as string) || null,
			kelurahan: (sh.kelurahan as string) || null,
			kecamatan: (sh.kecamatan as string) || null,
			city: (sh.kota as string) || null,
			rt: (sh.rt as string) || null,
			rw: (sh.rw as string) || null,
			status: sh.status != null ? parseInt(String(sh.status), 10) : null,
			isShareholder: sh.isShareholder as boolean | null ?? null,
			shareholding: sh.persentaseKepemilikan != null ? Number(sh.persentaseKepemilikan) : null,
		})),
		legalDocuments: ((values['legalitasDokumen'] as Record<string, unknown>[]) ?? []).map((doc) => ({
			documentType: (doc.jenis as string) || null,
			documentNumber: (doc.noDokumen as string) || null,
			documentDate: doc.tanggalDokumen && typeof doc.tanggalDokumen === 'object' && 'format' in (doc.tanggalDokumen as object)
				? (doc.tanggalDokumen as dayjs.Dayjs).format('YYYY-MM-DD')
				: null,
			notes: (doc.notes as string) || null,
		})),
	};
};
